<template>
  <form v-if="production" class="prod-stack" @submit.prevent="onSave">
    <div v-if="production.ready_for_release_at" class="prod-banner prod-banner--ready">
      <span class="material-icons">verified</span>
      Marked ready {{ formatDateTime(production.ready_for_release_at) }}
    </div>
    <div class="move-card prod-panel space-y-5">
      <ProductionSectionHead
        title="Ready for release"
        subtitle="Mark it ready when the final cut is approved, and keep the live links here."
      />
      <div class="move-field !mb-0">
        <label for="release-at">Scheduled release</label>
        <input id="release-at" v-model="form.scheduled_release_at" type="datetime-local" class="move-input" />
      </div>
      <div class="move-field !mb-0">
        <label for="release-notes">Notes</label>
        <textarea id="release-notes" v-model="form.release_notes" rows="4" class="move-textarea" />
      </div>

      <div>
        <p class="text-sm font-semibold move-text mb-3">Published links</p>
        <p v-if="!platforms.length" class="text-sm move-text-faint">
          Choose platforms first, then add the live URLs.
          <router-link to="platforms" class="font-semibold" style="color: var(--move-accent)">Open platforms</router-link>
        </p>
        <div v-else class="space-y-3">
          <div v-for="key in platforms" :key="key" class="move-field !mb-0">
            <label :for="`release-link-${key}`">{{ platformLabels[key] }}</label>
            <div class="flex gap-2">
              <input
                :id="`release-link-${key}`"
                v-model="links[key]"
                type="text"
                inputmode="url"
                autocomplete="url"
                class="move-input"
                :placeholder="`${platformLabels[key]} URL`"
              />
              <a
                v-if="links[key]"
                :href="links[key]"
                target="_blank"
                rel="noopener"
                class="move-btn-ghost shrink-0"
                :aria-label="`Open ${platformLabels[key]}`"
              >
                <span class="material-icons">open_in_new</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <p v-if="message" class="prod-success">{{ message }}</p>
      <div class="prod-form-actions">
        <button type="submit" class="move-btn-ghost">Save</button>
        <button type="button" class="move-btn-primary" @click="markReady">Mark ready for release</button>
      </div>
    </div>
  </form>
</template>

<script setup>
import { computed, inject, reactive, ref, watch } from 'vue'
import ProductionSectionHead from '../../components/production/ProductionSectionHead.vue'
import { platformLabels } from '../../config/productionNavigation'
import { formatDateTime, toDatetimeLocal } from '../../utils/productionDates'

const workspace = inject('productionWorkspace')
const production = workspace.production
const message = ref('')
const form = reactive({ scheduled_release_at: '', release_notes: '' })
const links = reactive({})

const platforms = computed(() => production.value?.platforms || [])

watch(production, (value) => {
  if (!value) return
  form.scheduled_release_at = toDatetimeLocal(value.scheduled_release_at)
  form.release_notes = value.release_notes || ''
  Object.keys(links).forEach((key) => { delete links[key] })
  Object.entries(value.release_links || {}).forEach(([key, url]) => {
    links[key] = url
  })
}, { immediate: true })

function releasePayload () {
  const releaseLinks = {}
  platforms.value.forEach((key) => {
    if (links[key]) releaseLinks[key] = links[key]
  })
  return {
    scheduled_release_at: form.scheduled_release_at || null,
    release_notes: form.release_notes || null,
    release_links: releaseLinks
  }
}

async function onSave () {
  await workspace.save(releasePayload())
  message.value = 'Release details saved.'
}

async function markReady () {
  await workspace.save({
    status: 'ready_for_release',
    ...releasePayload()
  })
  message.value = 'Marked ready for release.'
}
</script>
